import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { cx } from '../internal/cx';

/**
 * Casca da aplicação autenticada (§19).
 *
 * Anatomia do mandato: Sidebar (header/conteúdo/rodapé) + SidebarInset (SiteHeader + main).
 * A Sidebar vive sobre Ink — no brandbook o Ink é a camada de "infraestrutura", e é
 * exatamente esse o papel da navegação: a moldura estável em volta do trabalho.
 *
 * **Sem react-router aqui de propósito.** `@argink/ui` não depende de roteador (nem o
 * package declara essa dependência), e um design system que sabe navegar deixa de ser
 * reutilizável. A navegação entra como `ReactNode` — quem monta os links é o app, que é
 * quem conhece as rotas.
 *
 * O estado do offcanvas mobile é interno por padrão, porque na esmagadora maioria das telas
 * ninguém precisa controlá-lo de fora. Quem precisar (fechar a gaveta ao trocar de rota,
 * por exemplo) passa `sidebarOpen` + `onSidebarOpenChange` e o componente vira controlado —
 * o mesmo contrato de um `<input>`.
 */
export interface AppShellProps {
  /** Topo da sidebar — marca/produto. */
  sidebarHeader?: ReactNode;
  /** Navegação principal. O app monta os links. */
  sidebarContent: ReactNode;
  /** Rodapé da sidebar — normalmente o usuário. */
  sidebarFooter?: ReactNode;
  /** Contexto da página no header (título, breadcrumb). */
  headerContent?: ReactNode;
  /** Ações da página, à direita do header. */
  headerActions?: ReactNode;
  children: ReactNode;
  /** Controlado: estado da gaveta no mobile. Omitir deixa o componente gerenciar sozinho. */
  sidebarOpen?: boolean;
  onSidebarOpenChange?: (open: boolean) => void;
  className?: string;
  /**
   * Ícone do gatilho da gaveta (mobile). O DS **não depende de nenhuma biblioteca
   * de ícone** — nem `@fortawesome`, nem `lucide-react` — de propósito (R-UX02-P01):
   * um design system que importa um pacote de ícone deixa de ser neutro de stack. Quem
   * tiver uma biblioteca (`apps/web` usa `lucide-react`) passa o ícone já resolvido
   * aqui; sem ele, o botão fica só com o nome acessível (`aria-label`), nunca com um
   * glifo Unicode desenhado à mão — essa era a lacuna que este slot fecha.
   */
  sidebarTriggerIcon?: ReactNode;
  /**
   * Tema explícito do ambiente autenticado (`'light' | 'dark'`). Omitir segue a
   * preferência do sistema — a decisão de QUAIS cores cada tema usa vive inteira no
   * CSS do design system (`styles/tokens.css`), escopada a `.argink-app-shell` (este
   * componente) para nunca vazar tema para fora do ambiente autenticado: Home pública
   * e Login não renderizam `AppShell`, então não podem ser afetados por este prop.
   */
  theme?: 'light' | 'dark';
}

export function AppShell({
  sidebarHeader,
  sidebarContent,
  sidebarFooter,
  headerContent,
  headerActions,
  children,
  sidebarOpen,
  onSidebarOpenChange,
  className,
  sidebarTriggerIcon,
  theme,
}: AppShellProps) {
  const sidebarId = useId();
  const sidebarRef = useRef<HTMLElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = sidebarOpen !== undefined;
  const open = isControlled ? sidebarOpen : internalOpen;

  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onSidebarOpenChange?.(next);
  };

  /* Esc fecha a gaveta. É a expectativa de qualquer superfície sobreposta, e sem isso o
   * teclado fica preso atrás de um overlay que só o mouse sabe dispensar. O listener só
   * existe enquanto a gaveta está aberta. */
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Gerência de foco da gaveta.
   *
   * Sem isto a navegação por teclado tem dois buracos reais:
   *
   *  1. **Ao abrir**, o foco fica no gatilho. Como o `<aside>` vem ANTES do inset na ordem
   *     do DOM, o próximo Tab salta para o conteúdo principal — o usuário abre a navegação
   *     e o teclado nunca chega nela. Por isso o foco entra na sidebar ao abrir.
   *  2. **Ao fechar**, o CSS aplica `visibility: hidden`. Se o foco estivesse dentro da
   *     sidebar, ele evapora para o `<body>` e a pessoa perde o lugar na página. Por isso
   *     o foco volta para o gatilho, que é de onde ele saiu.
   *
   * Só age quando o gatilho está de fato visível: no desktop a sidebar é navegação
   * permanente e mover foco programaticamente seria um sequestro sem motivo. `offsetParent`
   * é nulo justamente quando o CSS aplicou `display: none` no gatilho (o breakpoint ≥1024px).
   */
  useEffect(() => {
    const isDrawerMode = triggerRef.current?.offsetParent !== null;

    if (open && !wasOpen.current && isDrawerMode) {
      sidebarRef.current?.focus();
    } else if (!open && wasOpen.current && isDrawerMode) {
      triggerRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  /**
   * Com a gaveta aberta, o conteúdo atrás dela sai do alcance do TECLADO também.
   *
   * O scrim bloqueia só o ponteiro. Sem isto, quem navega por Tab passava dos links
   * da navegação e continuava tabulando para o gatilho, o CTA do header e a página
   * inteira **por trás** de um overlay — chegando a acionar uma ação de fundo com a
   * navegação ainda aberta por cima. `inert` remove a subárvore da tabulação, do
   * clique e da árvore de acessibilidade de uma vez; é o que um `<dialog>` modal faz
   * com o resto da página.
   *
   * Só no modo gaveta: no desktop a sidebar é navegação permanente e inertizar o
   * conteúdo ali tornaria a aplicação inoperável. `offsetParent` nulo é exatamente
   * quando o CSS escondeu o gatilho (o breakpoint ≥1024px).
   *
   * Aplicado por `setAttribute` e não por prop JSX porque `inert` só virou atributo
   * de primeira classe no React 19 — assim o comportamento não depende da versão que
   * o app usa.
   */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    /**
     * Tudo o que a gaveta cobre — **incluindo o link de pulo**, que vive ANTES do
     * `<aside>` no DOM. Sem ele na lista, `Shift+Tab` a partir do primeiro item da
     * navegação escapava para um link que aponta para `#conteudo`, justamente a
     * região inerte: o teclado saía da gaveta por cima e ia parar em lugar nenhum.
     *
     * O gatilho fica de fora de propósito: enquanto a gaveta está aberta ele é o
     * botão "Fechar navegação", ou seja, a própria saída.
     *
     * **Limite conhecido:** isto contém o que está DENTRO do shell. Conteúdo levado
     * para fora por `createPortal` (um modal ancorado em `document.body`, por
     * exemplo) continuaria alcançável com a gaveta aberta. Hoje o produto não tem
     * nenhum portal — nem `apps/web` nem `@argink/ui` chamam `createPortal`, e o
     * `Toast` documenta a decisão de não usar um. Fechar esse buraco pede um focus
     * trap de verdade (ou `inert` no `<body>` com o shell exceptuado), que é escopo
     * de design system, não desta rodada. Registrado para não virar surpresa quando
     * o primeiro modal aparecer.
     */
    const covered = () =>
      root.querySelectorAll<HTMLElement>(
        '.argink-app-shell__skip, .argink-app-shell__header-content, .argink-app-shell__header-actions, .argink-app-shell__main',
      );

    /**
     * Recalculado a cada `resize`, e não só quando `open` muda.
     *
     * Abrir a gaveta no celular e alargar a janela até o desktop deixava o `inert`
     * grudado: o gatilho some no breakpoint, `open` continua `true`, e a aplicação
     * inteira ficava inoperante atrás de uma sidebar que ali é permanente e não cobre
     * nada. O teste é `offsetParent` do gatilho — o próprio CSS decidindo o que é modo
     * gaveta —, para o breakpoint não precisar ser repetido em JavaScript.
     */
    const apply = () => {
      const isDrawerMode = triggerRef.current?.offsetParent !== null;
      const shouldBlock = open && isDrawerMode;
      covered().forEach((el) => {
        if (shouldBlock) el.setAttribute('inert', '');
        else el.removeAttribute('inert');
      });
    };

    apply();
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      covered().forEach((el) => el.removeAttribute('inert'));
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={cx('argink-app-shell', open && 'argink-app-shell--open', className)}
      {...(theme ? { 'data-theme': theme } : {})}
    >
      {/* Fora da tela até receber foco — a sidebar tem muitos links e o teclado precisa
       * poder saltá-los. */}
      <a className="argink-app-shell__skip" href="#conteudo">
        Pular para o conteúdo
      </a>

      <aside
        id={sidebarId}
        ref={sidebarRef}
        /* -1: recebe foco por programa (ao abrir a gaveta), nunca por Tab — a sidebar não
         * deve virar uma parada extra na ordem de tabulação do desktop. */
        tabIndex={-1}
        className="argink-app-shell__sidebar"
        /* No desktop a sidebar é navegação permanente; no mobile ela é uma gaveta.
         * `data-collapsed` é o gancho para o CSS aplicar `visibility: hidden` — que, ao
         * contrário de `opacity` ou de um `transform` para fora da tela, REALMENTE remove o
         * elemento da ordem de tabulação. Sem isso a gaveta sumiria da vista e continuaria
         * tabulável, e um foco que sai da tela é pior do que nenhuma gaveta. O CSS reverte
         * isso no breakpoint de desktop, onde a sidebar precisa ser tabulável sempre. */
        {...(open ? {} : { 'data-collapsed': 'true' })}
      >
        {sidebarHeader ? (
          <div className="argink-app-shell__sidebar-header">{sidebarHeader}</div>
        ) : null}
        <nav className="argink-app-shell__sidebar-content" aria-label="Navegação principal">
          {sidebarContent}
        </nav>
        {sidebarFooter ? (
          <div className="argink-app-shell__sidebar-footer">{sidebarFooter}</div>
        ) : null}
      </aside>

      {/* Só existe no mobile com a gaveta aberta; fechar clicando fora é esperado.
       *
       * **Fora da árvore de acessibilidade de propósito.** Ele é uma conveniência de
       * PONTEIRO — a mesma ação já existe, para teclado e leitor de tela, no gatilho
       * ("Fechar navegação") e no Esc. Anunciá-lo colocava DOIS botões com o mesmo
       * nome na tela ao mesmo tempo, indistinguíveis para quem só ouve a interface.
       * `tabIndex={-1}` o mantém fora da tabulação, que é o que a regra
       * `aria-hidden-focus` exige de um elemento escondido. */}
      {open ? (
        <button
          type="button"
          className="argink-app-shell__scrim"
          aria-hidden="true"
          tabIndex={-1}
          onClick={() => setOpen(false)}
        />
      ) : null}

      <div className="argink-app-shell__inset">
        <header className="argink-app-shell__header">
          <button
            ref={triggerRef}
            type="button"
            className="argink-app-shell__trigger argink-focusable"
            aria-expanded={open}
            aria-controls={sidebarId}
            onClick={() => setOpen(!open)}
          >
            {sidebarTriggerIcon ? (
              <span aria-hidden="true">{sidebarTriggerIcon}</span>
            ) : null}
            <span className="argink-sr-only">
              {open ? 'Fechar navegação' : 'Abrir navegação'}
            </span>
          </button>
          {headerContent ? (
            <div className="argink-app-shell__header-content">{headerContent}</div>
          ) : null}
          {headerActions ? (
            <div className="argink-app-shell__header-actions">{headerActions}</div>
          ) : null}
        </header>

        {/* `tabIndex={-1}`: sem isto, o link de pulo move o SCROLL até aqui mas o
         * foco do teclado vai para o `<body>` — `<main>` não é naturalmente
         * focável, e um fragmento apontando para um alvo não-focável não move
         * `document.activeElement` (WHATWG HTML, "focusing steps"). -1 o torna
         * focável POR PROGRAMA (o fragmento), sem entrar na ordem de Tab
         * sequencial — o mesmo padrão já usado na sidebar (linha ~215 acima). */}
        <main id="conteudo" className="argink-app-shell__main" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}
